import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { Separator } from '../components/ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, Tooltip as RechartsTooltip, LineChart, Line
} from 'recharts';
import {
  Clock, Timer, TrendingUp, Users, RefreshCw, CalendarDays,
  ArrowDown, ArrowUp, Minus, Activity, AlertTriangle, CheckCircle2,
  XCircle, Zap, Download
} from 'lucide-react';
import { toast } from 'sonner';

const RANGE_OPTIONS = [
  { value: 'day', label: 'Today' },
  { value: 'week', label: 'This Week' },
  { value: 'month', label: 'This Month' },
];

const STATUS_COLORS = {
  completed: '#059669',
  scheduled: '#3B82F6',
  arrived: '#0EA5E9',
  checking_in: '#06B6D4',
  ready: '#0D9488',
  in_visit: '#7C3AED',
  cancelled: '#EF4444',
  no_show: '#F59E0B',
};

const CHART_COLORS = ['#2563EB', '#059669', '#D97706', '#7C3AED', '#0D9488', '#EF4444'];

function formatMinutes(mins) {
  if (!mins && mins !== 0) return '--';
  if (mins < 60) return `${Math.round(mins)}m`;
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function MetricCard({ title, value, unit, subtitle, icon: Icon, trend, color }) {
  return (
    <Card className="transition-shadow duration-150 hover:shadow-md" data-testid={`metric-card-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[11px] text-muted-foreground font-medium uppercase tracking-wide">{title}</p>
            <div className="flex items-baseline gap-1 mt-1">
              <span className="text-2xl font-bold tabular-nums" style={{ fontFamily: 'Space Grotesk', color }}>
                {value}
              </span>
              {unit && <span className="text-xs text-muted-foreground">{unit}</span>}
            </div>
            {subtitle && <p className="text-[10px] text-muted-foreground mt-0.5">{subtitle}</p>}
          </div>
          <div className="flex flex-col items-end gap-1">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center`} style={{ backgroundColor: `${color}15` }}>
              <Icon className="w-4 h-4" style={{ color }} />
            </div>
            {trend !== undefined && trend !== null && (
              <div className={`flex items-center gap-0.5 text-[10px] font-medium ${
                trend > 0 ? 'text-red-500' : trend < 0 ? 'text-emerald-500' : 'text-muted-foreground'
              }`}>
                {trend > 0 ? <ArrowUp className="w-3 h-3" /> : trend < 0 ? <ArrowDown className="w-3 h-3" /> : <Minus className="w-3 h-3" />}
                {trend !== 0 ? `${Math.abs(trend)}%` : 'flat'}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ProviderComparisonChart({ data }) {
  if (!data || data.length === 0) return null;

  const chartData = data.map(p => ({
    name: p.provider_name.split(' ').slice(-1)[0],
    fullName: p.provider_name,
    'Avg Wait': p.avg_wait_time,
    'Avg Visit': p.avg_visit_duration,
    'Throughput': p.avg_total_throughput,
    count: p.completed_count,
  }));

  return (
    <Card data-testid="provider-comparison-chart">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm" style={{ fontFamily: 'Space Grotesk' }}>Provider Comparison</CardTitle>
        <CardDescription className="text-xs">Average times in minutes by provider</CardDescription>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={chartData} barGap={2}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis dataKey="name" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 10 }} unit="m" />
            <RechartsTooltip
              contentStyle={{
                fontSize: 11,
                borderRadius: 8,
                border: '1px solid hsl(var(--border))',
                backgroundColor: 'hsl(var(--card))',
              }}
              formatter={(value, name) => [`${value} min`, name]}
              labelFormatter={(label, payload) => payload?.[0]?.payload?.fullName || label}
            />
            <Bar dataKey="Avg Wait" fill="#F59E0B" radius={[3, 3, 0, 0]} maxBarSize={32} />
            <Bar dataKey="Avg Visit" fill="#2563EB" radius={[3, 3, 0, 0]} maxBarSize={32} />
            <Bar dataKey="Throughput" fill="#059669" radius={[3, 3, 0, 0]} maxBarSize={32} />
            <Legend wrapperStyle={{ fontSize: 10 }} />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

function StatusDistributionChart({ data }) {
  if (!data || Object.keys(data).length === 0) return null;

  const chartData = Object.entries(data).map(([status, count]) => ({
    name: status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
    value: count,
    color: STATUS_COLORS[status] || '#6B7280',
  }));

  return (
    <Card data-testid="status-distribution-chart">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm" style={{ fontFamily: 'Space Grotesk' }}>Status Distribution</CardTitle>
        <CardDescription className="text-xs">Appointment outcomes breakdown</CardDescription>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={220}>
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              innerRadius={55}
              outerRadius={85}
              paddingAngle={2}
              dataKey="value"
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <RechartsTooltip
              contentStyle={{
                fontSize: 11,
                borderRadius: 8,
                border: '1px solid hsl(var(--border))',
                backgroundColor: 'hsl(var(--card))',
              }}
              formatter={(value, name) => [value, name]}
            />
            <Legend wrapperStyle={{ fontSize: 10 }} />
          </PieChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

function DailyTrendChart({ data }) {
  if (!data || data.length <= 1) return null;

  const chartData = data.map(d => ({
    date: new Date(d.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
    'Wait Time': d.avg_wait,
    'Visit Time': d.avg_visit,
    'Completed': d.completed,
  }));

  return (
    <Card data-testid="daily-trend-chart">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm" style={{ fontFamily: 'Space Grotesk' }}>Daily Trends</CardTitle>
        <CardDescription className="text-xs">Average times and volume over the selected period</CardDescription>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis dataKey="date" tick={{ fontSize: 10 }} />
            <YAxis yAxisId="time" tick={{ fontSize: 10 }} unit="m" />
            <YAxis yAxisId="count" orientation="right" tick={{ fontSize: 10 }} />
            <RechartsTooltip
              contentStyle={{
                fontSize: 11,
                borderRadius: 8,
                border: '1px solid hsl(var(--border))',
                backgroundColor: 'hsl(var(--card))',
              }}
            />
            <Line yAxisId="time" type="monotone" dataKey="Wait Time" stroke="#F59E0B" strokeWidth={2} dot={{ r: 3 }} />
            <Line yAxisId="time" type="monotone" dataKey="Visit Time" stroke="#2563EB" strokeWidth={2} dot={{ r: 3 }} />
            <Line yAxisId="count" type="monotone" dataKey="Completed" stroke="#059669" strokeWidth={2} dot={{ r: 3 }} strokeDasharray="4 2" />
            <Legend wrapperStyle={{ fontSize: 10 }} />
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

function RecentVisitsTable({ visits }) {
  if (!visits || visits.length === 0) return null;

  return (
    <Card data-testid="recent-visits-table">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm" style={{ fontFamily: 'Space Grotesk' }}>Recent Completed Visits</CardTitle>
        <CardDescription className="text-xs">Individual visit timing breakdown</CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b bg-muted/30">
                <th className="text-left p-2.5 font-medium text-muted-foreground">Patient</th>
                <th className="text-left p-2.5 font-medium text-muted-foreground">Provider</th>
                <th className="text-left p-2.5 font-medium text-muted-foreground">Type</th>
                <th className="text-right p-2.5 font-medium text-muted-foreground">Check-in</th>
                <th className="text-right p-2.5 font-medium text-muted-foreground">Wait</th>
                <th className="text-right p-2.5 font-medium text-muted-foreground">Visit</th>
                <th className="text-right p-2.5 font-medium text-muted-foreground">Total</th>
              </tr>
            </thead>
            <tbody>
              {visits.slice(0, 15).map((v, idx) => (
                <tr key={v.appointment_id || idx} className="border-b last:border-0 hover:bg-accent/30 transition-colors">
                  <td className="p-2.5 font-medium">{v.patient_name}</td>
                  <td className="p-2.5 text-muted-foreground">{v.provider_name?.split(' ').slice(-1)[0]}</td>
                  <td className="p-2.5">
                    <Badge variant="outline" className="text-[10px]">{v.visit_type}</Badge>
                  </td>
                  <td className="p-2.5 text-right tabular-nums">{formatMinutes(v.checkin_time_min)}</td>
                  <td className="p-2.5 text-right tabular-nums">
                    <span className={v.wait_time_min > 20 ? 'text-amber-600 font-medium' : ''}>
                      {formatMinutes(v.wait_time_min)}
                    </span>
                  </td>
                  <td className="p-2.5 text-right tabular-nums">{formatMinutes(v.visit_duration_min)}</td>
                  <td className="p-2.5 text-right tabular-nums font-medium">{formatMinutes(v.total_throughput_min)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

export default function VisitMetrics() {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [rangeType, setRangeType] = useState('week');
  const [providerId, setProviderId] = useState('all');
  const [providers, setProviders] = useState([]);

  const fetchMetrics = useCallback(async () => {
    setLoading(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const params = new URLSearchParams({ date: today, range_type: rangeType });
      if (providerId && providerId !== 'all') params.append('provider_id', providerId);

      const [metricsRes, provRes] = await Promise.all([
        api.get(`/analytics/visit-metrics?${params.toString()}`),
        api.get('/providers'),
      ]);
      setMetrics(metricsRes.data);
      setProviders(provRes.data.providers || []);
    } catch (err) {
      toast.error('Failed to load metrics');
    } finally {
      setLoading(false);
    }
  }, [rangeType, providerId]);

  useEffect(() => { fetchMetrics(); }, [fetchMetrics]);

  const handleSeedDemo = async () => {
    setSeeding(true);
    try {
      const res = await api.post('/analytics/seed-demo-metrics');
      toast.success(res.data.message);
      fetchMetrics();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to seed demo data');
    } finally {
      setSeeding(false);
    }
  };

  const hasData = metrics && metrics.counts?.completed > 0;

  if (loading) {
    return (
      <div className="p-4 sm:p-6 max-w-6xl space-y-4 animate-fadeIn">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-24" />)}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <Skeleton className="h-64" />
          <Skeleton className="h-64" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-6xl animate-fadeIn" data-testid="visit-metrics-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
            <TrendingUp className="w-5 h-5 text-primary" /> Visit Efficiency
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Track wait times, visit duration, and throughput across providers
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={rangeType} onValueChange={setRangeType}>
            <SelectTrigger className="h-8 w-32 text-xs" data-testid="metrics-range-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RANGE_OPTIONS.map(r => (
                <SelectItem key={r.value} value={r.value} className="text-xs">{r.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={providerId} onValueChange={setProviderId}>
            <SelectTrigger className="h-8 w-44 text-xs" data-testid="metrics-provider-select">
              <SelectValue placeholder="All Providers" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" className="text-xs">All Providers</SelectItem>
              {providers.map(p => (
                <SelectItem key={p.id} value={p.id} className="text-xs">
                  {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetchMetrics} data-testid="metrics-refresh-button">
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      {/* Date range info */}
      {metrics?.date_range && (
        <div className="flex items-center gap-2 mb-4">
          <CalendarDays className="w-3.5 h-3.5 text-muted-foreground" />
          <span className="text-xs text-muted-foreground">
            {new Date(metrics.date_range.start + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            {metrics.date_range.start !== metrics.date_range.end && (
              <> - {new Date(metrics.date_range.end + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</>
            )}
          </span>
          <Separator orientation="vertical" className="h-3" />
          <span className="text-xs text-muted-foreground">
            {metrics.counts?.total || 0} appointments | {metrics.counts?.completed || 0} completed
          </span>
        </div>
      )}

      {/* Empty State */}
      {!hasData && (
        <Card className="mt-6">
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-14 h-14 rounded-2xl bg-muted flex items-center justify-center mb-4">
              <Activity className="w-7 h-7 text-muted-foreground" />
            </div>
            <h3 className="text-base font-semibold mb-1" style={{ fontFamily: 'Space Grotesk' }}>
              No completed visits found
            </h3>
            <p className="text-sm text-muted-foreground max-w-md mb-5">
              Visit metrics are calculated from completed appointments with timing data.
              As patients are checked in and visits are completed, metrics will populate automatically.
            </p>
            <Button onClick={handleSeedDemo} disabled={seeding} variant="outline" data-testid="seed-demo-button">
              <Zap className="w-4 h-4 mr-1.5" />
              {seeding ? 'Seeding...' : 'Load Demo Data'}
            </Button>
            <p className="text-[10px] text-muted-foreground mt-2">
              Seeds a week of realistic appointment data for demonstration
            </p>
          </CardContent>
        </Card>
      )}

      {/* Metrics Content */}
      {hasData && (
        <div className="space-y-4">
          {/* KPI Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="metrics-kpi-cards">
            <MetricCard
              title="Avg Wait Time"
              value={formatMinutes(metrics.aggregates?.avg_wait_time)}
              subtitle={`Max: ${formatMinutes(metrics.aggregates?.max_wait_time)}`}
              icon={Clock}
              color="#F59E0B"
            />
            <MetricCard
              title="Avg Check-in"
              value={formatMinutes(metrics.aggregates?.avg_checkin_time)}
              subtitle="Arrival to ready"
              icon={CheckCircle2}
              color="#0D9488"
            />
            <MetricCard
              title="Avg Visit"
              value={formatMinutes(metrics.aggregates?.avg_visit_duration)}
              subtitle={`Max: ${formatMinutes(metrics.aggregates?.max_visit_duration)}`}
              icon={Timer}
              color="#2563EB"
            />
            <MetricCard
              title="Total Throughput"
              value={formatMinutes(metrics.aggregates?.avg_total_throughput)}
              subtitle="Arrival to checkout"
              icon={TrendingUp}
              color="#059669"
            />
            <MetricCard
              title="Completion Rate"
              value={metrics.counts?.completed > 0
                ? `${Math.round((metrics.counts.completed / Math.max(1, metrics.counts.total - (metrics.counts.cancelled || 0))) * 100)}%`
                : '0%'}
              subtitle={`${metrics.counts?.no_show || 0} no-shows, ${metrics.counts?.cancelled || 0} cancelled`}
              icon={Users}
              color="#7C3AED"
            />
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <ProviderComparisonChart data={metrics.provider_summary} />
            <StatusDistributionChart data={metrics.status_distribution} />
          </div>

          {/* Trend Chart (only for week/month) */}
          {rangeType !== 'day' && (
            <DailyTrendChart data={metrics.daily_summary} />
          )}

          {/* Provider Cards */}
          {metrics.provider_summary?.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold mb-2" style={{ fontFamily: 'Space Grotesk' }}>
                Provider Breakdown
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {metrics.provider_summary.map(ps => {
                  const prov = providers.find(p => p.id === ps.provider_id);
                  return (
                    <Card key={ps.provider_id} className="transition-shadow duration-150 hover:shadow-md" data-testid={`provider-metric-${ps.provider_name.replace(/\s/g, '-').toLowerCase()}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: prov?.color || '#6B7280' }} />
                          <span className="text-sm font-medium">{ps.provider_name}</span>
                          <Badge variant="outline" className="text-[10px] ml-auto">{ps.completed_count} visits</Badge>
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          <div className="text-center">
                            <p className="text-[10px] text-muted-foreground">Wait</p>
                            <p className="text-sm font-bold tabular-nums text-amber-600">{formatMinutes(ps.avg_wait_time)}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-[10px] text-muted-foreground">Visit</p>
                            <p className="text-sm font-bold tabular-nums text-blue-600">{formatMinutes(ps.avg_visit_duration)}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-[10px] text-muted-foreground">Total</p>
                            <p className="text-sm font-bold tabular-nums text-emerald-600">{formatMinutes(ps.avg_total_throughput)}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          )}

          {/* Recent Visits Table */}
          <RecentVisitsTable visits={metrics.visit_details} />
        </div>
      )}
    </div>
  );
}
